import axios from "axios";

/**
 * Derive the auth service base URL (scheme + host + port) from the API base URL.
 * Auth endpoints live at /auth/*, outside the /api/v1 prefix.
 * @returns {string} e.g. "http://localhost:3000"
 */
const getAuthBaseUrl = () => {
  const apiBase = import.meta.env.VITE_API_BASE_URL;
  try {
    const { origin } = new URL(apiBase);
    return origin;
  } catch {
    return apiBase;
  }
};

/**
 * Redirect the browser to the backend's Azure AD login initiator.
 * The backend redirects to Azure AD; after authentication Azure AD
 * redirects back to the backend callback, which sets an HTTP-only session
 * cookie and redirects the browser back to the requested frontend URL.
 * @param {string} [returnTo] - Optional absolute frontend URL to return to.
 */
export const initiateLogin = (returnTo) => {
  const authLoginUrl = new URL("auth/login", `${getAuthBaseUrl()}/`);
  if (returnTo) {
    authLoginUrl.searchParams.set("returnTo", returnTo);
  }
  window.location.href = authLoginUrl.toString();
};

/**
 * Fetch the current user from the active server-side session.
 * Returns null when the session is absent or expired (HTTP 401).
 * @returns {Promise<{id: string, name: string, email: string}|null>}
 */
export const getMe = async () => {
  try {
    const meUrl = new URL("auth/me", `${getAuthBaseUrl()}/`).toString();
    const { data } = await axios.get(meUrl, {
      withCredentials: true,
    });
    return data.authenticated ? data.user : null;
  } catch (error) {
    if (error?.response?.status === 401) {
      return null;
    }
    throw error;
  }
};

/**
 * Log out the current user by destroying the server-side session via a
 * form POST. The backend destroys the session cookie and redirects the
 * browser back to the frontend.
 */
export const logout = () => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = new URL("auth/logout", `${getAuthBaseUrl()}/`).toString();

  const returnToInput = document.createElement("input");
  returnToInput.type = "hidden";
  returnToInput.name = "returnTo";
  returnToInput.value = window.location.href;
  form.appendChild(returnToInput);

  document.body.appendChild(form);
  form.submit();
};
